import * as React from 'react';

import { createHeadingLink } from 'af-utils/userGuideModal.util';

import { SearchInput as DesignSearchInput } from '@acceligentllc/storybook';

import { ModalLink } from 'af-components/ModalNavigation/ModalRouter';

import { icons } from './routes';
import type { SearchPageResults } from './types';

interface Props {
	initialText?: string;
	onFilterTextChange: (filterText: string) => void;
	onSearch: (text: string) => void;
	dropdownSearchedResults?: SearchPageResults;
	getModalBodyBottomPositionForRecalculation: () => number | undefined;
}

interface LinkHeading {
	link: string;
	icon?: string;
	text: string;
}

interface LinkedHeadings {
	[heading: string]: {
		[heading: string]: {
			linkedHeading: LinkHeading;
		};
	};
}

interface LinkedHeadingsWithBreadcrumbs {
	[mainPage: string]: { // Section that is a main page
		[heading: string]: { // Either a page (orange heading) or a subpage (blue headings)
			headings: LinkHeading[]; // Subtitles (Bold non-colored headings)
			breadcrumbs: string[];
		};
	};
}

const scrollbarEndOffset = 10;

const SearchInput: React.FC<Props> = (props) => {
	const { onFilterTextChange, onSearch, dropdownSearchedResults, initialText, getModalBodyBottomPositionForRecalculation } = props;

	const [filterText, setFilterText] = React.useState(initialText ?? '');
	const [inputFocused, setInputFocused] = React.useState(false);
	const [isDropdownAllowed, setIsDropdownAllowed] = React.useState(false);
	const [dropdownHeight, setDropdownHeight] = React.useState(0);

	const preventDropdownHideRef = React.useRef<boolean>(false);
	const inputRef = React.useRef<HTMLInputElement | null>(null);
	const dropdownRef = React.useRef<HTMLDivElement | null>(null);
	const quickReleseCoords = React.useRef<{ x: number; y: number; }>(undefined); // Needed because existing logic does not handle mouse click and relese on the scrollbar

	React.useEffect(() => {
		setFilterText(initialText ?? '');
	}, [initialText]);

	const handleFocus = React.useCallback(() => {
		setInputFocused(true);
		setIsDropdownAllowed(true);
	}, []);

	const handleBlur = React.useCallback(() => {
		setTimeout(() => {
			if (preventDropdownHideRef.current) {
				preventDropdownHideRef.current = false;
				return;
			}
			setInputFocused(false);
			setIsDropdownAllowed(true);
		}, 200);
	}, []);

	const handleClickInsideDropdown = React.useCallback(() => {
		preventDropdownHideRef.current = true;
		inputRef.current?.focus();
	}, []);

	React.useEffect(() => {
		const mouseDown = (e: MouseEvent) => {
			quickReleseCoords.current = { x: e.x, y: e.y };
		};

		const mouseUp = (e: MouseEvent) => {
			const boundingBox = dropdownRef.current?.getBoundingClientRect();
			if (!boundingBox || !quickReleseCoords.current) {
				return;
			}
			if (e.x !== quickReleseCoords.current.x || e.y !== quickReleseCoords.current.y) {
				return;
			}
			if (boundingBox.left + boundingBox.width - scrollbarEndOffset < quickReleseCoords.current.x &&
				boundingBox.left + boundingBox.width > quickReleseCoords.current.x &&
				boundingBox.top < quickReleseCoords.current.y &&
				boundingBox.top + boundingBox.height > quickReleseCoords.current.y
			) {
				preventDropdownHideRef.current = true;
				inputRef.current?.focus();
			}
		};
		window.addEventListener('mousedown', mouseDown);
		window.addEventListener('mouseup', mouseUp);

		return () => {
			window.removeEventListener('mousedown', mouseDown);
			window.removeEventListener('mouseup', mouseUp);
		};

	}, []);

	// We want it to execute after dropdown click
	const handleClickOnLink = React.useCallback(() => {
		setTimeout(() => {
			setFilterText('');
			onFilterTextChange('');
			preventDropdownHideRef.current = false;
			inputRef.current?.blur();
		}, 100);
	}, [onFilterTextChange]);

	const handleInputChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement> | string) => {
		const currFilterText = typeof e === 'string' ? e : e.target.value;
		setFilterText(currFilterText);
		onFilterTextChange(currFilterText);
	}, [onFilterTextChange]);

	const _onSearch = React.useCallback(() => {
		onSearch(filterText);
	}, [filterText, onSearch]);

	const handleKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			_onSearch();
			inputRef.current?.blur();
		}
	}, [_onSearch]);

	const linkedHeadings = React.useMemo(() => {
		if (!dropdownSearchedResults) return {};
		const headingByPage: LinkedHeadings = {};
		for (const page of Object.keys(dropdownSearchedResults)) {
			const pageKeyWithoutFolder = page.split('/')[1];

			if (!headingByPage[pageKeyWithoutFolder]) {
				headingByPage[pageKeyWithoutFolder] = {};
			}

			for (const searchResult of Object.values(dropdownSearchedResults[page])) {
				if (!searchResult.depth) {
					continue; // We only want headings
				}

				const resultPageWithoutFolder = searchResult.page.split('/')[1];

				if (searchResult.subPage) {
					if (!headingByPage[pageKeyWithoutFolder][searchResult.subPage]) {
						headingByPage[resultPageWithoutFolder][searchResult.subPage] = {
							linkedHeading: {
								link: createHeadingLink(pageKeyWithoutFolder, searchResult.subPage, 'subpage'),
								icon: icons[pageKeyWithoutFolder],
								text: searchResult.subPage,
							},
						};
					}
					continue;
				}

				if (!headingByPage[pageKeyWithoutFolder][resultPageWithoutFolder]) {
					headingByPage[resultPageWithoutFolder][resultPageWithoutFolder] = {
						linkedHeading: {
							link: createHeadingLink(pageKeyWithoutFolder, resultPageWithoutFolder, 'page'),
							icon: icons[pageKeyWithoutFolder],
							text: resultPageWithoutFolder.split('_').join(' '),
						},
					};
				}
				continue;
			}

			if (Object.keys(headingByPage[pageKeyWithoutFolder]).length === 0) {
				delete headingByPage[pageKeyWithoutFolder];
			}
		}

		return headingByPage;
	}, [dropdownSearchedResults]);

	const renderHeadingLinks = React.useMemo(() => {
		const headingsByMainPage = linkedHeadings;

		return Object.values(headingsByMainPage).map((sections) => {
			return Object.values(sections).map((headingLinkData) => {
				return (
					<div className="user-guide__search-link" key={headingLinkData.linkedHeading.link}>
						<span className={`${headingLinkData.linkedHeading.icon}`}></span>
						<ModalLink link={`${headingLinkData.linkedHeading.link}`} onClick={handleClickOnLink}>
							<span className="heading">{headingLinkData.linkedHeading.text}</span>
						</ModalLink>
					</div>
				);
			});
		});

	}, [handleClickOnLink, linkedHeadings]);

	const subtitleSearchSections = React.useMemo(() => {
		if (!dropdownSearchedResults) return {};
		const _sections: LinkedHeadingsWithBreadcrumbs = {};
		for (const page of Object.keys(dropdownSearchedResults)) {
			const pageKeyWithoutFolder = page.split('/')[1];

			if (!_sections[pageKeyWithoutFolder]) {
				_sections[pageKeyWithoutFolder] = {};
			}
			for (const searchResult of Object.values(dropdownSearchedResults[page])) {
				if (!searchResult.depth || !searchResult.subTitle || !searchResult.subTitle.toLowerCase().includes(filterText.toLowerCase())) {
					continue; // We only want subtitle headings
				}

				const resultPageWithoutFolder = searchResult.page.split('/')[1];

				const breadcrumbs: string[] = [resultPageWithoutFolder, searchResult.subPage].filter((section): section is string => typeof section === 'string');

				if (searchResult.subPage) {
					if (!_sections[pageKeyWithoutFolder][searchResult.subPage]) {
						_sections[pageKeyWithoutFolder][searchResult.subPage] = {
							headings: [],
							breadcrumbs,
						};
					}
					_sections[resultPageWithoutFolder][searchResult.subPage].headings.push({
						link: createHeadingLink(pageKeyWithoutFolder, searchResult.subTitle, 'subtitle'),
						text: searchResult.subTitle,
					});
					continue;
				}

				if (!_sections[pageKeyWithoutFolder][resultPageWithoutFolder]) {
					_sections[resultPageWithoutFolder][resultPageWithoutFolder] = {
						headings: [],
						breadcrumbs,
					};
				}
				_sections[pageKeyWithoutFolder][resultPageWithoutFolder].headings.push({
					link: createHeadingLink(pageKeyWithoutFolder, searchResult.subTitle, 'subtitle'),
					text: searchResult.subTitle,
				});
				continue;
			}

			if (Object.keys(_sections[pageKeyWithoutFolder]).length === 0) {
				delete _sections[pageKeyWithoutFolder];
			}
		}

		return _sections;

	}, [dropdownSearchedResults, filterText]);

	const renderSubtitleSearches = React.useMemo(() => {
		const _sections = subtitleSearchSections;

		return Object.keys(_sections).map((page) => {
			return (
				<div className="section-container" key={page}>
					{<div className="section">
						{page.split('_').join(' ').toUpperCase()}
					</div>}

					{/* The sections are main pages */}
					{Object.values(_sections[page]).map((section) => {

						{ /* Each section has some number of subtitles that are part of some heading sections that are shown as breadcrumbs*/ }
						return section.headings.map((subtitle) => {
							return (
								<div key={subtitle.text + section}>
									<div className="subtitle-container">
										<ModalLink link={`${subtitle.link}`} onClick={handleClickOnLink}>
											<span className="subtitle">{subtitle.text}</span>
										</ModalLink>
									</div>
									{section.breadcrumbs.length > 0 && <div className="subtitle-breadcrumbs">
										{section.breadcrumbs[0].split('_').join(' ')}
										{section.breadcrumbs.slice(1).map((crumb) => {
											return (
												<div key={crumb}>
													<span className="icon-right"></span>
													{crumb}
												</div>
											);
										})}
									</div>}
								</div>
							);
						});
					})}
				</div>
			);
		});

	}, [handleClickOnLink, subtitleSearchSections]);

	const showDropdown = React.useMemo(() => {
		return inputFocused && ((renderHeadingLinks ?? []).length > 0 || (renderSubtitleSearches ?? []).length > 0) && isDropdownAllowed;
	}, [inputFocused, isDropdownAllowed, renderHeadingLinks, renderSubtitleSearches]);

	React.useLayoutEffect(() => {
		const recalculateBy = (dropdownRef.current?.getBoundingClientRect().top ?? 0) + window.scrollY;
		setDropdownHeight((getModalBodyBottomPositionForRecalculation() ?? 0) - recalculateBy);
	}, [getModalBodyBottomPositionForRecalculation, showDropdown]);

	// FIXME: use Input from storybook
	return (
		<div>
			<div className={'user-guide__search-input'} >
				<DesignSearchInput
					name="searchQuery"
					onBlur={handleBlur}
					onChange={handleInputChange}
					onFocus={handleFocus}
					onKeyDown={handleKeyDown}
					placeholder="Search"
					value={filterText}
				/>
			</div>
			{showDropdown && <div className="user-guide__search-dropdown-container">
				<div className="user-guide__search-dropdown"
					onClick={handleClickInsideDropdown}
					onScroll={handleClickInsideDropdown}
					ref={dropdownRef}
					style={{ height: dropdownHeight }}
				>
					<div>
						{renderHeadingLinks}
					</div>
					<div className="user-guide__search-subtitles">
						{renderSubtitleSearches}
					</div>
				</div>
			</div>}
		</div>
	);
};

export default React.memo(SearchInput);
